import { useState, useEffect } from "react";
import { api, ApiError } from "../api/client";

interface DealCreateFormProps {
  onClose: () => void;
  onSuccess: () => void;
  dictionaries: {
    companies: Array<{ id: number; name: string }>;
    banks_by_company: Record<number, Array<{ id: number; name: string }>>;
    counterparties: Array<{ id: number; name: string }>;
    operators: Array<{ id: number; name: string }>;
    subcontractors: Array<{ id: number; name: string }>;
    subcontractor_banks_by_sub: Record<
      number,
      Array<{ id: number; name: string }>
    >;
  };
}

type DealFormat = "REGULAR" | "DEFERRED_RATE" | "PRESET_RATE";
type DealType = "IMPORT" | "EXPORT";

const CURRENCIES = [
  "RUB",
  "USD",
  "EUR",
  "GBP",
  "CNY",
  "AED",
  "SGD",
  "JPY",
  "CHF",
  "CAD",
  "AUD",
];

export function DealCreateForm({
  onClose,
  onSuccess,
  dictionaries,
}: DealCreateFormProps) {
  const [dealType, setDealType] = useState<DealType>("IMPORT");
  const [baseCurrency, setBaseCurrency] = useState("USD");
  const [currency, setCurrency] = useState("RUB");
  const [amount, setAmount] = useState("");
  const [feePercent, setFeePercent] = useState("");
  const [hasFixedCommission, setHasFixedCommission] = useState(false);
  const [fixedAmount, setFixedAmount] = useState("");
  const [fixedCurrency, setFixedCurrency] = useState("RUB");
  const [companyId, setCompanyId] = useState<number | "">(
    dictionaries.companies[0]?.id || ""
  );
  const [bankId, setBankId] = useState<number | "">(
    dictionaries.banks_by_company[companyId as number]?.[0]?.id || ""
  );
  const [dealFormat, setDealFormat] = useState<DealFormat>("REGULAR");
  const [counterpartyId, setCounterpartyId] = useState<number | "">(
    dictionaries.counterparties[0]?.id || ""
  );
  const [subcontractorId, setSubcontractorId] = useState<number | "">(
    dictionaries.subcontractors[0]?.id || ""
  );
  const [subcontractorBankId, setSubcontractorBankId] = useState<number | "">(
    ""
  );
  const [rate, setRate] = useState("");
  const [rateModifierPct, setRateModifierPct] = useState("");
  const [deadline, setDeadline] = useState("");
  const [plannedFixDate, setPlannedFixDate] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update banks when company changes
  useEffect(() => {
    const company = companyId as number;
    if (company && dictionaries.banks_by_company[company]) {
      setBankId(dictionaries.banks_by_company[company][0]?.id || "");
    } else {
      setBankId("");
    }
  }, [companyId, dictionaries]);

  // Update subcontractor banks when subcontractor changes
  useEffect(() => {
    const sub = subcontractorId as number;
    if (sub && dictionaries.subcontractor_banks_by_sub[sub]) {
      setSubcontractorBankId(dictionaries.subcontractor_banks_by_sub[sub][0]?.id || "");
    } else {
      setSubcontractorBankId("");
    }
  }, [subcontractorId, dictionaries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!amount || !feePercent) {
        throw new Error("Заполните обязательные поля: сумма и комиссия");
      }

      if (hasFixedCommission && !fixedAmount) {
        throw new Error("Если фиксированная комиссия, укажите сумму");
      }

      const payload = {
        deal_type: dealType.toUpperCase(),
        base_currency: baseCurrency.toUpperCase(),
        currency: currency.toUpperCase(),
        amount: parseFloat(amount),
        fee_percent: parseFloat(feePercent),
        fixed_commission_amount: hasFixedCommission
          ? parseFloat(fixedAmount)
          : null,
        fixed_commission_currency: hasFixedCommission ? fixedCurrency.toUpperCase() : null,
        company_id: companyId,
        bank_id: bankId || null,
        deal_format: dealFormat.toUpperCase(),
        counterparty_id: counterpartyId || null,
        subcontractor_id: subcontractorId || null,
        subcontractor_bank_id: subcontractorBankId || null,
        rate: rate ? parseFloat(rate) : null,
        rate_modifier_pct: rateModifierPct ? parseFloat(rateModifierPct) : null,
        deadline: deadline || null,
        planned_fix_date: plannedFixDate || null,
        notes: notes || null,
      };

      const response = await api.post<{ id: number }>("/api/deals", payload);
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.message || "Ошибка при создании заявки");
    } finally {
      setLoading(false);
    }
  };

  const companyBanks =
    companyId && dictionaries.banks_by_company[companyId as number]
      ? dictionaries.banks_by_company[companyId as number]
      : [];

  const subBanks =
    subcontractorId && dictionaries.subcontractor_banks_by_sub[subcontractorId as number]
      ? dictionaries.subcontractor_banks_by_sub[subcontractorId as number]
      : [];

  return (
    <div className="fixed inset-0 bg-black flex items-center justify-center z-50 p-4">
      <div className="bg-tg-secondaryBg rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-tg-sectionBg border-b border-tg-hint/20 px-4 py-3 flex justify-between items-center">
          <h2 className="text-lg font-bold text-tg-text">Создать заявку</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-tg-hint hover:text-tg-text text-xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 text-red-400 rounded text-sm">
              {error}
            </div>
          )}

          {/* Deal Type */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Тип заявки *
            </label>
            <select
              value={dealType}
              onChange={(e) => setDealType(e.target.value as DealType)}
              className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text"
            >
              <option value="IMPORT">Импорт</option>
              <option value="EXPORT">Экспорт</option>
            </select>
          </div>

          {/* Base Currency */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Базовая валюта *
              </label>
              <select
                value={baseCurrency}
                onChange={(e) => setBaseCurrency(e.target.value)}
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Currency */}
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Валюта платежа *
              </label>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Amount & Fee */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Сумма ({currency}) *
              </label>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Комиссия % *
              </label>
              <input
                type="number"
                step="0.01"
                value={feePercent}
                onChange={(e) => setFeePercent(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text"
              />
            </div>
          </div>

          {/* Fixed Commission */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hasFixedCommission}
                onChange={(e) => setHasFixedCommission(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm text-tg-text">
                Фиксированная комиссия
              </span>
            </label>
          </div>

          {hasFixedCommission && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium text-tg-text mb-1">
                  Сумма комиссии *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={fixedAmount}
                  onChange={(e) => setFixedAmount(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-tg-text mb-1">
                  Валюта комиссии *
                </label>
                <select
                  value={fixedCurrency}
                  onChange={(e) => setFixedCurrency(e.target.value)}
                  className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Company & Bank */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Компания *
              </label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              >
                {dictionaries.companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Банк
              </label>
              <select
                value={bankId}
                onChange={(e) =>
                  setBankId(e.target.value ? parseInt(e.target.value) : "")
                }
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              >
                <option value="">Выбрать банк</option>
                {companyBanks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Deal Format */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Формат заявки *
            </label>
            <select
              value={dealFormat}
              onChange={(e) => setDealFormat(e.target.value as DealFormat)}
              className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text"
            >
              <option value="REGULAR">Обычная</option>
              <option value="DEFERRED_RATE">Отложенная фиксация курса</option>
              <option value="PRESET_RATE">С предустановленным курсом</option>
            </select>
          </div>

          {/* Counterparty */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Контрагент
            </label>
            <select
              value={counterpartyId}
              onChange={(e) =>
                setCounterpartyId(e.target.value ? parseInt(e.target.value) : "")
              }
              className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
            >
              <option value="">Выбрать контрагента</option>
              {dictionaries.counterparties.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Subcontractor */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Субподрядчик
              </label>
              <select
                value={subcontractorId}
                onChange={(e) =>
                  setSubcontractorId(
                    e.target.value ? parseInt(e.target.value) : ""
                  )
                }
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              >
                <option value="">Выбрать субподрядчика</option>
                {dictionaries.subcontractors.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {subBanks.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-tg-text mb-1">
                  Банк субподрядчика
                </label>
                <select
                  value={subcontractorBankId}
                  onChange={(e) =>
                    setSubcontractorBankId(
                      e.target.value ? parseInt(e.target.value) : ""
                    )
                  }
                  className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
                >
                  <option value="">Выбрать банк</option>
                  {subBanks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Rate & Modifier */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Курс (опционально)
              </label>
              <input
                type="number"
                step="0.0001"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.0000"
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Модификатор курса % (опционально)
              </label>
              <input
                type="number"
                step="0.01"
                value={rateModifierPct}
                onChange={(e) => setRateModifierPct(e.target.value)}
                placeholder="0.00"
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Срок оплаты (опционально)
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Плановая фиксация (опционально)
              </label>
              <input
                type="date"
                value={plannedFixDate}
                onChange={(e) => setPlannedFixDate(e.target.value)}
                className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Примечания (опционально)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Комментарий к заявке..."
              rows={3}
              className="w-full px-3 py-2 bg-tg-sectionBg border border-tg-hint/30 rounded text-tg-text text-sm"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-tg-hint/20">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-2 bg-tg-sectionBg text-tg-text rounded font-medium disabled:opacity-50"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-tg-button text-tg-buttonText rounded font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Создание..." : "Создать заявку"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
